// mds-dojo — dijit/layout/ContentPane — FAITHFUL port of
// references/dijit/layout/ContentPane.js (overwrites the earlier freestyle
// version). AMD → ESM + classic declare from the faithful runtime; only the
// module's final `return declare(...)` becomes `export default`.
//
// Dep mapping / stubs:
//   dojo/html         → ./_html      (local port — _ContentSetter + parser wiring)
//   dojo/_base/Deferred → ./_Deferred (local synchronous Deferred)
//   dojo/_base/xhr    → local xhr.get() adapter over ../../dojo/request/xhr
//                       (href-load path is best-effort — see TODO in _load/cancel)
//   dojo/i18n!../nls/loading → inlined message table (i18n plugin skipped)
import kernel from '../../dojo/_base/kernel';
import lang from '../../dojo/_base/lang';
import _Widget from '../_Widget';
import _Container from '../_Container';
import _ContentPaneResizeMixin from './_ContentPaneResizeMixin';
import string from '../../dojo/string';
import html from '../../dojo/html';
import array from '../../dojo/_base/array';
import declare from '../../dojo/_base/declare-runtime';
import Deferred from '../../dojo/_base/Deferred';
import dom from '../../dojo/dom';
import domConstruct from '../../dojo/dom-construct';
import requestXhr from '../../dojo/request/xhr';
import when from '../../dojo/when';

// dojo/_base/xhr.get() shim over dojo/request/xhr. STUB-DEP: dojo/_base/xhr isn't
// ported; the returned promise lacks the legacy .fired/.cancel/.canceled surface,
// so ContentPane.cancel() can't actually abort an in-flight fetch (guarded to be a
// no-op rather than crash). TODO: port dojo/_base/xhr if true cancellation matters.
var xhr: any = {
  get: function (args: any) {
    return requestXhr(args.url, {
      handleAs: args.handleAs || 'text',
      preventCache: args.preventCache
    });
  }
};

// dojo/i18n!../nls/loading — inlined (see references/dijit/nls/loading.js root)
var loadingMessages = {
  loadingState: 'Loading...',
  errorState: 'Sorry, an error occurred'
};

export default declare('dijit.layout.ContentPane', [_Widget, _Container, _ContentPaneResizeMixin], {
  // summary:
  //		A widget containing an HTML fragment, specified inline or by uri.

  // href: String
  href: '',

  // content: String|DomNode|NodeList|dijit/_Widget
  content: '',

  // extractContent: Boolean
  extractContent: false,

  // parseOnLoad: Boolean
  parseOnLoad: true,

  // parserScope: String
  parserScope: kernel._scopeName,

  // preventCache: Boolean
  preventCache: false,

  // preload: Boolean
  preload: false,

  // refreshOnShow: Boolean
  refreshOnShow: false,

  // loadingMessage: String
  loadingMessage: "<span class='dijitContentPaneLoading'><span class='dijitInline dijitIconLoading'></span>${loadingState}</span>",

  // errorMessage: String
  errorMessage: "<span class='dijitContentPaneError'><span class='dijitInline dijitIconError'></span>${errorState}</span>",

  // isLoaded: [readonly] Boolean
  isLoaded: false,

  baseClass: 'dijitContentPane',

  // ioArgs: Object
  ioArgs: {},

  // onLoadDeferred: [readonly] dojo.Deferred
  onLoadDeferred: null,

  // Cancel _WidgetBase's _setTitleAttr (title = tab label, not a tooltip).
  _setTitleAttr: null,

  // Flag to parser that I'll parse my contents, so it shouldn't.
  stopParser: true,

  // template: [private] Boolean
  template: false,

  markupFactory: function (params: any, node: any, ctor: any) {
    var self = new ctor(params, node);

    // If a parse has started but is waiting for modules, return a Promise for when
    // the parser finishes. (mds parser is synchronous / its thenable may lack
    // isFulfilled(), so guard that probe.)
    var pd = self._contentSetter && self._contentSetter.parseDeferred;
    return !self.href && pd && (!pd.isFulfilled || !pd.isFulfilled()) ?
      pd.then(function () {
        return self;
      }) : self;
  },

  create: function (params: any, srcNodeRef: any) {
    // Convert a srcNodeRef argument into a content parameter.
    if ((!params || !params.template) && srcNodeRef && !('href' in params) && !('content' in params)) {
      srcNodeRef = dom.byId(srcNodeRef);
      var df = srcNodeRef.ownerDocument.createDocumentFragment();
      while (srcNodeRef.firstChild) {
        df.appendChild(srcNodeRef.firstChild);
      }
      params = lang.delegate(params, { content: df });
    }
    this.inherited(arguments, [params, srcNodeRef]);
  },

  postMixInProperties: function () {
    this.inherited(arguments);
    var messages = loadingMessages;
    this.loadingMessage = string.substitute(this.loadingMessage, messages);
    this.errorMessage = string.substitute(this.errorMessage, messages);
  },

  buildRendering: function () {
    this.inherited(arguments);

    // Since we have no template we need to set this.containerNode ourselves.
    if (!this.containerNode) {
      this.containerNode = this.domNode;
    }

    // remove the title attribute so it doesn't show up when hovering
    this.domNode.removeAttribute('title');
  },

  startup: function () {
    // Call startup() on all children including non _Widget ones.
    this.inherited(arguments);

    // And this catches stuff like dojo/dnd/Source
    if (this._contentSetter) {
      array.forEach(this._contentSetter.parseResults, function (obj: any) {
        if (!obj._started && !obj._destroyed && lang.isFunction(obj.startup)) {
          obj.startup();
          obj._started = true;
        }
      }, this);
    }
  },

  _startChildren: function () {
    // Called when content is loaded. Calls startup on each child widget.
    array.forEach(this.getChildren(), function (obj: any) {
      if (!obj._started && !obj._destroyed && lang.isFunction(obj.startup)) {
        obj.startup();
        obj._started = true;
      }
    });

    // And this catches stuff like dojo/dnd/Source
    if (this._contentSetter) {
      array.forEach(this._contentSetter.parseResults, function (obj: any) {
        if (!obj._started && !obj._destroyed && lang.isFunction(obj.startup)) {
          obj.startup();
          obj._started = true;
        }
      });
    }
  },

  setHref: function (href: any) {
    // Deprecated. Use set('href', ...) instead.
    if (kernel.deprecated) {
      kernel.deprecated("dijit.layout.ContentPane.setHref() is deprecated. Use set('href', ...) instead.", '', '2.0');
    }
    return this.set('href', href);
  },
  _setHrefAttr: function (href: any) {
    // Hook so set("href", ...) works.

    // Cancel any in-flight requests
    this.cancel();

    this.onLoadDeferred = new Deferred(lang.hitch(this, 'cancel'));
    this.onLoadDeferred.then(lang.hitch(this, 'onLoad'));

    this._set('href', href);

    // Assuming preload == false, only load now if shown; otherwise defer.
    if (this.preload || (this._created && this._isShown())) {
      this._load();
    } else {
      this._hrefChanged = true;
    }

    return this.onLoadDeferred;		// Deferred
  },

  setContent: function (data: any) {
    // Deprecated. Use set('content', ...) instead.
    if (kernel.deprecated) {
      kernel.deprecated("dijit.layout.ContentPane.setContent() is deprecated.  Use set('content', ...) instead.", '', '2.0');
    }
    this.set('content', data);
  },
  _setContentAttr: function (data: any) {
    // Hook to make set("content", ...) work.

    // clear href so we can't run refresh and clear content
    this._set('href', '');

    // Cancel any in-flight requests
    this.cancel();

    // Even though user is setting content directly, still need an onLoadDeferred
    this.onLoadDeferred = new Deferred(lang.hitch(this, 'cancel'));
    if (this._created) {
      // For back-compat reasons, call onLoad() for set('content', ...)
      this.onLoadDeferred.then(lang.hitch(this, 'onLoad'));
    }

    this._setContent(data || '');

    this._isDownloaded = false; // mark that content is from a set('content')

    return this.onLoadDeferred;	// Deferred
  },
  _getContentAttr: function () {
    // Hook to make get("content") work
    return this.containerNode.innerHTML;
  },

  cancel: function () {
    // Cancels an in-flight download of content.
    // NOTE (stub): the request/xhr promise lacks the legacy .fired == -1 probe, so
    // this guarded check is effectively a no-op abort. See xhr.get shim above.
    if (this._xhrDfd && (this._xhrDfd.fired == -1)) {
      this._xhrDfd.cancel();
    }
    delete this._xhrDfd; // garbage collect

    this.onLoadDeferred = null;
  },

  destroy: function () {
    this.cancel();
    this.inherited(arguments);
  },

  destroyRecursive: function (preserveDom: any) {
    // Destroy the ContentPane and its contents

    // if we have multiple controllers destroying us, bail after the first
    if (this._beingDestroyed) {
      return;
    }
    this.inherited(arguments);
  },

  _onShow: function () {
    // Called when the ContentPane is made visible.
    this.inherited(arguments);

    if (this.href) {
      if (!this._xhrDfd && // if there's an href that isn't already being loaded
        (!this.isLoaded || this._hrefChanged || this.refreshOnShow)
      ) {
        return this.refresh();	// promise that fires when the load is complete
      }
    }
  },

  refresh: function () {
    // [Re]download contents of href and display

    // Cancel possible prior in-flight request
    this.cancel();

    this.onLoadDeferred = new Deferred(lang.hitch(this, 'cancel'));
    this.onLoadDeferred.then(lang.hitch(this, 'onLoad'));
    this._load();
    return this.onLoadDeferred;		// promise that fires when refresh is complete
  },

  _load: function () {
    // Load/reload the href specified in this.href

    // display loading message
    this._setContent(this.onDownloadStart(), true);

    var self = this;
    var getArgs: any = {
      preventCache: (this.preventCache || this.refreshOnShow),
      url: this.href,
      handleAs: 'text'
    };
    if (lang.isObject(this.ioArgs)) {
      lang.mixin(getArgs, this.ioArgs);
    }

    var hand = (this._xhrDfd = (this.ioMethod || xhr.get)(getArgs)),
      returnedHtml: any;

    hand.then(
      function (html: any) {
        returnedHtml = html;
        try {
          self._isDownloaded = true;
          return self._setContent(html, false);
        } catch (err: any) {
          self._onError('Content', err); // onContentError
        }
      },
      function (err: any) {
        if (!hand.canceled) {
          // show error message in the pane
          self._onError('Download', err); // onDownloadError
        }
        delete self._xhrDfd;
        return err;
      }
    ).then(function () {
      self.onDownloadEnd();
      delete self._xhrDfd;
      return returnedHtml;
    });

    // Remove flag saying that a load is needed
    delete this._hrefChanged;
  },

  _onLoadHandler: function (data: any) {
    // This is called whenever new content is being loaded
    this._set('isLoaded', true);
    try {
      this.onLoadDeferred.resolve(data);
    } catch (e: any) {
      console.error('Error ' + (this.widgetId || this.id) + ' running custom onLoad code: ' + e.message);
    }
  },

  _onUnloadHandler: function () {
    // This is called whenever the content is being unloaded
    this._set('isLoaded', false);
    try {
      this.onUnload();
    } catch (e: any) {
      console.error('Error ' + this.widgetId + ' running custom onUnload code: ' + e.message);
    }
  },

  destroyDescendants: function (preserveDom: any) {
    // Destroy all the widgets inside the ContentPane and empty containerNode

    // Make sure we call onUnload (but only when the ContentPane has real content)
    if (this.isLoaded) {
      this._onUnloadHandler();
    }

    // For historical reasons we need to delete all widgets under this.containerNode.
    var setter = this._contentSetter;
    array.forEach(this.getChildren(), function (widget: any) {
      if (widget.destroyRecursive) {
        widget.destroyRecursive(preserveDom);
      } else if (widget.destroy) {
        widget.destroy(preserveDom);
      }
      widget._destroyed = true;
    });
    if (setter) {
      // Most of the widgets in setter.parseResults have already been destroyed, but
      // things like Menu that have been moved to <body> haven't yet
      array.forEach(setter.parseResults, function (widget: any) {
        if (!widget._destroyed) {
          if (widget.destroyRecursive) {
            widget.destroyRecursive(preserveDom);
          } else if (widget.destroy) {
            widget.destroy(preserveDom);
          }
          widget._destroyed = true;
        }
      });
      delete setter.parseResults;
    }

    // And then clear away all the DOM nodes
    if (!preserveDom) {
      domConstruct.empty(this.containerNode);
    }

    // Delete any state information we have about current contents
    delete this._singleChild;
  },

  _setContent: function (cont: any, isFakeContent: any) {
    // Insert the content into the container node.

    cont = this.preprocessContent(cont);
    // first get rid of child widgets
    this.destroyDescendants();

    // html.set will take care of the rest of the details
    var setter = this._contentSetter;
    if (!(setter && setter instanceof html._ContentSetter)) {
      setter = this._contentSetter = new html._ContentSetter({
        node: this.containerNode,
        _onError: lang.hitch(this, this._onError),
        onContentError: lang.hitch(this, function (e: any) {
          // fires if a domfault occurs when we are appending this.errorMessage
          var errMess = this.onContentError(e);
          try {
            this.containerNode.innerHTML = errMess;
          } catch (e2: any) {
            console.error('Fatal ' + this.id + ' could not change content due to ' + e2.message, e2);
          }
        })
      });
    }

    var setterParams = lang.mixin({
      cleanContent: this.cleanContent,
      extractContent: this.extractContent,
      parseContent: !cont.domNode && this.parseOnLoad,
      parserScope: this.parserScope,
      startup: false,
      dir: this.dir,
      lang: this.lang,
      textDir: this.textDir
    }, this._contentSetterParams || {});

    var p = setter.set((lang.isObject(cont) && cont.domNode) ? cont.domNode : cont, setterParams);

    // If set() returns a promise then use it, otherwise wait on setter.parseDeferred
    var self = this;
    return when(p && p.then ? p : setter.parseDeferred, function () {
      // setter params must be pulled afresh from the ContentPane each time
      delete self._contentSetterParams;

      if (!isFakeContent) {
        if (self._started) {
          // Startup each top level child widget (recursively)
          self._startChildren();

          // Call resize() on each of my child layout widgets
          self._scheduleLayout();
        }
        self._onLoadHandler(cont);
      }
    });
  },

  preprocessContent: function (content: any) {
    // Hook, called after content has loaded, before being processed.
    return content;
  },

  _onError: function (type: any, err: any, consoleText: any) {
    this.onLoadDeferred.reject(err);

    // shows user the string returned by on[type]Error
    var errText = this['on' + type + 'Error'].call(this, err);
    if (consoleText) {
      console.error(consoleText, err);
    } else if (errText) { // an empty string won't change current content
      this._setContent(errText, true);
    }
  },

  // EVENT's, should be overide-able
  onLoad: function (/*===== data =====*/) {
    // Event hook, is called after everything is loaded and widgetified
  },

  onUnload: function () {
    // Event hook, is called before old content is cleared
  },

  onDownloadStart: function () {
    // Called before download starts.
    return this.loadingMessage;
  },

  onContentError: function (/*Error*/ /*===== error =====*/) {
    // Called on DOM faults, require faults etc. in content.
  },

  onDownloadError: function (/*Error*/ /*===== error =====*/) {
    // Called when download error occurs.
    return this.errorMessage;
  },

  onDownloadEnd: function () {
    // Called when download is finished.
  }
});
